/**
 * SSE Client — the one authed Server-Sent Events streaming primitive.
 *
 * Uses fetch + ReadableStream (not EventSource) for POST request support and
 * AbortController integration. Auth is handled under the hood: the access
 * token is attached as a Bearer header, and a pre-stream 401 triggers ONE
 * silent token refresh + reconnect (you can't replay a stream mid-flight, so
 * the refresh-and-retry happens before the stream starts). This is the single
 * place agent streaming talks auth — callers never touch tokens.
 *
 * Lives in shared/lib/api/ because BOTH chat (POST stream) and copilot (GET
 * stream) consume it; pulling it into shared eliminates the shared→feature
 * import asymmetry that would otherwise exist after the workspace rebuild.
 */

import { getAccessToken } from "@/features/auth/lib/session";
import { tryRefresh } from "@/shared/lib/api/client";
import { NetworkError } from "@/features/auth/api";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080";

export interface SSECallbacks<T> {
  onEvent: (event: T) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface SSEOptions<T> extends SSECallbacks<T> {
  signal?: AbortSignal;
}

/**
 * Stream SSE events from a POST endpoint. Generic over the event type so the
 * caller gets a typed `onEvent` (agent chat passes AgentEvent).
 *
 * Parses `data: {...}\n\n` frames, line-buffered for partial chunks.
 */
export async function streamSSE<T = unknown>(
  endpoint: string,
  body: unknown,
  options: SSEOptions<T>,
): Promise<void> {
  const { onEvent, onError, onComplete, signal } = options;
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  const open = (): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    const token = getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
      signal,
    });
  };

  try {
    let response = await open();

    // Pre-stream 401 → one silent refresh + reconnect. (No mid-stream retry:
    // a started stream can't be replayed.)
    if (response.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        response = await open();
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":")) continue;

        if (trimmed.startsWith("data: ")) {
          const jsonStr = trimmed.slice(6);
          if (jsonStr === "[DONE]") continue;

          try {
            const event = JSON.parse(jsonStr) as T;
            onEvent(event);
          } catch {
            // Silently ignore malformed JSON events
          }
        }
      }
    }

    onComplete?.();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    onError?.(error instanceof Error ? error : new Error(String(error)));
  }
}

export interface SSEGetHandle {
  close(): void;
  done: Promise<void>;
}

/**
 * Stream SSE events from a GET endpoint. Same auth handling as streamSSE —
 * a pre-stream 401 triggers one silent refresh + reconnect; a NetworkError
 * during refresh surfaces as a fatal error to the caller.
 *
 * Parses line-buffered `data: {...}` frames. Skips comments and empty lines.
 */
export function streamGetSSE<T = unknown>(
  endpoint: string,
  onEvent: (event: T) => void,
  onError?: (error: Error) => void,
): SSEGetHandle {
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;
  const controller = new AbortController();

  const open = (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    const token = getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, {
      method: "GET",
      headers,
      credentials: "include",
      signal: controller.signal,
    });
  };

  const done = (async () => {
    try {
      let response = await open();

      if (response.status === 401) {
        const refreshed = await tryRefresh();
        if (refreshed) response = await open();
      }

      if (!response.ok || !response.body) {
        const errorText = response.body ? await response.text() : "";
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done: rDone, value } = await reader.read();
        if (rDone) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const raw of lines) {
          const line = raw.trim();
          if (!line || line.startsWith(":") || !line.startsWith("data:")) continue;
          const payload = line.slice(5).trimStart();
          if (!payload || payload === "[DONE]") continue;
          try {
            onEvent(JSON.parse(payload) as T);
          } catch {
            // ignore malformed frames
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      if (err instanceof NetworkError) {
        onError?.(err);
        return;
      }
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return {
    close: () => controller.abort(),
    done,
  };
}
